import React from 'react';
import { get, map } from 'lodash';
import { Box } from '@mui/material';
import { Chip } from 'cprime-foundational-components';
import Link from 'components/Link/Link';
import PrintChip from 'components/PrintChip/PrintChip';
import TypographyMarked from 'components/TypographyMarked/TypographyMarked';
import { generateTestId, isVehicleDetailsDataExist } from 'utils/general';
import { formatDataValue } from 'utils/formatFns';
import { chipColorMap } from 'utils/colorMappers';
import { getSharedLabelAndValue, getSharedLabelsAndValues } from '../sharedData/sharedDataConfig';
import TooltipWrapper from 'components/TooltipWrapper/TooltipWrapper';

const TOOLTIP_TEXT = 'Current Status';

export const getCurrentStatusChip = (
  currentStatus,
  indicator,
  isPrint,
  searchContextValues,
  isShowTooltip,
) => {
  const WrapperComponent = isShowTooltip ? TooltipWrapper : React.Fragment;
  const wrapperProps = isShowTooltip ? { tooltipText: TOOLTIP_TEXT } : {};

  if (currentStatus?.includes('Allocation In Process')) {
    return isPrint ? (
      <PrintChip
        label="Allocation In Process"
        color={chipColorMap?.allocationInProcessText}
        backgroundColor={chipColorMap?.allocationInProcessBackground}
      />
    ) : (
      <WrapperComponent {...wrapperProps}>
        <Box>
          <Chip
            label={
              <TypographyMarked
                dataTestId={generateTestId('VehicleRecord', 'AssignmentStatusChip')}
                value={currentStatus}
                fieldId={`${currentStatus}-chip`}
                isPrint={isPrint}
                variant="label1"
                searchContextValues={searchContextValues}
              />
            }
            color={chipColorMap?.allocationInProcessText}
            backgroundColor={chipColorMap?.allocationInProcessBackground}
          />
        </Box>
      </WrapperComponent>
    );
  }
  if (currentStatus === 'Allocation Authorized') {
    return isPrint ? (
      <PrintChip
        label={currentStatus}
        color={chipColorMap?.allocationAuthorizedText}
        backgroundColor={chipColorMap?.allocationAuthorizedBackground}
      />
    ) : (
      <WrapperComponent {...wrapperProps}>
        <Box>
          <Chip
            label={
              <TypographyMarked
                dataTestId={generateTestId('VehicleRecord', 'AssignmentStatusChip')}
                value={currentStatus}
                fieldId={`${currentStatus}-chip`}
                isPrint={isPrint}
                variant="label1"
                searchContextValues={searchContextValues}
              />
            }
            color={chipColorMap?.allocationAuthorizedText}
            backgroundColor={chipColorMap?.allocationAuthorizedBackground}
          />
        </Box>
      </WrapperComponent>
    );
  }
  if (currentStatus === 'Assigned' || indicator) {
    return isPrint ? (
      <PrintChip
        label={currentStatus}
        color={chipColorMap?.allocationCompletedText}
        backgroundColor={chipColorMap?.allocationCompletedBackground}
      />
    ) : (
      <WrapperComponent {...wrapperProps}>
        <Box>
          <Chip
            label={
              <TypographyMarked
                dataTestId={generateTestId('VehicleRecord', 'AssignmentStatusChip')}
                value={currentStatus}
                fieldId={`${currentStatus}-chip`}
                isPrint={isPrint}
                variant="label1"
                searchContextValues={searchContextValues}
              />
            }
            color={chipColorMap?.allocationCompletedText}
            backgroundColor={chipColorMap?.allocationCompletedBackground}
          />
        </Box>
      </WrapperComponent>
    );
  }
  return isPrint ? (
    <PrintChip
      label="Unassigned"
      color={chipColorMap?.allocationUnassignedText}
      backgroundColor={chipColorMap?.allocationUnassignedBackground}
    />
  ) : (
    <WrapperComponent {...wrapperProps}>
      <Box>
        <Chip
          label={
            <TypographyMarked
              dataTestId={generateTestId('VehicleRecord', 'AssignmentStatusChip')}
              value={'Unassigned'}
              fieldId={`${'Unassigned'}-chip`}
              isPrint={isPrint}
              variant="label1"
              searchContextValues={searchContextValues}
            />
          }
          color={chipColorMap?.allocationUnassignedText}
          backgroundColor={chipColorMap?.allocationUnassignedBackground}
        />
      </Box>
    </WrapperComponent>
  );
};

const getVehicleAssignmentHeroData = ({
  vehicle,
  isPrint,
  searchContextValues,
  setSelectedTab,
}) => [
  ...getSharedLabelsAndValues(['currentStatus', 'allocationNumber'], vehicle, {
    isPrint,
    searchContextValues,
    isShowTooltip: false,
  }),
  ...(!isPrint
    ? [
        {
          label: 'Latest Assignment Date',
          value: (
            <Link
              label={formatDataValue({
                value:
                  vehicle?.generalInfo?.dealerAssignmentInfo?.allocationDateFmt ||
                  'Vehicle History',
              })}
              variant="body2"
              onClick={() => {
                window.scroll(0, 0);
                setSelectedTab('history');
              }}
            />
          ),
        },
      ]
    : []),
];

const getCurrentData = ({ vehicle }) => [
  {
    label: 'Area',
    value: `${formatDataValue({
      value: vehicle?.generalInfo?.dealerInfo?.currentRegionCode,
    })} | ${formatDataValue({
      value: vehicle?.generalInfo?.dealerInfo?.currentRegionName,
    })}`,
  },
  {
    label: 'Dealer',
    value: `${formatDataValue({
      value: vehicle?.generalInfo?.dealerInfo?.currentDealerCode,
    })} | ${formatDataValue({
      value: vehicle?.generalInfo?.dealerInfo?.currentDealerName,
    })}`,
  },
];

const getAreaData = ({ vehicle }) => [
  {
    label: 'Area',
    value: `${formatDataValue({
      value: vehicle?.generalInfo?.dealerInfo?.shipToRegionCode,
    })} | ${formatDataValue({
      value: vehicle?.generalInfo?.dealerInfo?.shipToRegionName,
    })}`,
  },
  {
    label: 'Dealer',
    value: `${formatDataValue({
      value: vehicle?.generalInfo?.dealerInfo?.shipToDealerCode,
    })} | ${formatDataValue({
      value: vehicle?.generalInfo?.dealerInfo?.shipToDealerName,
    })}`,
  },
];

const getRowAttributes = ({
  assignment,
  vehicle,
  index,
  setSelectedTab,
  isPrint,
  searchContextValues,
}) => [
  {
    id: 'allocation',
    value: (
      <>
        <TypographyMarked
          dataTestId={generateTestId('VehicleRecord', 'AssignmentAllocationNumberValue')}
          component="span"
          variant={isPrint ? 'print2' : 'body2'}
          value={`${
            index === 0
              ? formatDataValue({
                  value: vehicle?.generalInfo?.dealerAssignmentInfo?.allocationNumber,
                })
              : formatDataValue({
                  value: vehicle?.generalInfo?.dealerAssignmentInfo?.previousAllocationNumber,
                })
          } ${isPrint ? '' : ' | '}`}
          fieldId={`${index}-allocation`}
          isPrint={isPrint}
          searchContextValues={searchContextValues}
        />
        {!isPrint ? (
          <Link
            label={formatDataValue({
              value:
                vehicle?.generalInfo?.dealerAssignmentInfo?.allocationDateFmt || 'Vehicle History',
            })}
            onClick={() => {
              window.scroll(0, 0);
              setSelectedTab('history');
            }}
            variant="body2"
            id={`history-allocation-${index}`}
          />
        ) : null}
      </>
    ),
  },
  {
    id: 'area',
    value: `${
      index === 0
        ? formatDataValue({ value: vehicle?.generalInfo?.dealerInfo?.currentRegionCode })
        : formatDataValue({
            value: assignment?.regionCode,
          })
    } | ${
      index === 0
        ? formatDataValue({
            value: vehicle?.generalInfo?.dealerInfo?.currentRegionName,
          })
        : formatDataValue({
            value: assignment?.regionName,
          })
    }`,
  },
  {
    id: 'dealer',
    value: `${
      index === 0
        ? formatDataValue({ value: vehicle?.generalInfo?.dealerInfo?.currentDealerCode })
        : formatDataValue({
            value: assignment?.dealerCode,
          })
    } | ${
      index === 0
        ? formatDataValue({ value: vehicle?.generalInfo?.dealerInfo?.currentDealerName })
        : formatDataValue({
            value: assignment?.dealerName,
          })
    }`,
  },
  {
    id: 'source',
    value: `${formatDataValue({ value: assignment?.allocationSource })}`,
  },
  {
    id: 'status',
    value: index === 0 ? 'current' : '',
    component: 'statusChip',
  },
];

const getDealerTradeAttributes = (vehicle) =>
  getSharedLabelsAndValues(['pipelineProtection', 'comments', 'tradeType', 'empty'], vehicle);

const getDealerAllocationGmpYAttributes = (vehicle) =>
  getSharedLabelsAndValues(
    [
      'pipelineProtection',
      'gmpIndicator',
      'gmpAssignmentDealer',
      'comments',
      'allocationSeries',
      'empty',
      'gmpAssignmentArea',
      'empty',
    ],
    vehicle,
  );

const getNoDealerAllocationGmpNAttributes = (vehicle) => [
  getSharedLabelAndValue('pipelineProtection', vehicle),
  {
    label: 'GMP Indicator',
    value: 'N',
  },
  {
    label: 'GMP Assignment Dealer',
    value: 'N/A',
  },
  ...getSharedLabelsAndValues(['comments', 'allocationSeries', 'empty'], vehicle),
  {
    label: 'GMP Assignment Area',
    value: 'N/A',
  },
  {
    label: '',
    value: '',
  },
];

const getRowDetails = ({ vehicle, index, allocationSourceParsed }) => {
  if (!allocationSourceParsed) {
    return null;
  }

  const gmpCode = get(vehicle, 'generalInfo.dealerAssignmentInfo.gmpCode');

  const currentAllocationValue = `${
    index === 0
      ? formatDataValue({
          value: vehicle?.generalInfo?.dealerAssignmentInfo?.allocationNumber,
        })
      : formatDataValue({
          value: vehicle?.generalInfo?.dealerAssignmentInfo?.previousAllocationNumber,
        })
  }`;

  const title = {
    title: `${
      index === 0 ? 'Current' : 'Previous'
    } Allocation Attributes: ${currentAllocationValue}`,
    numberOfColumns: 2,
  };

  if (allocationSourceParsed === 'Dealer Trade') {
    return {
      attributes: getDealerTradeAttributes(vehicle),
      ...title,
    };
  } else if (allocationSourceParsed === 'Dealer Allocation' && gmpCode === true) {
    return {
      attributes: getDealerAllocationGmpYAttributes(vehicle),
      ...title,
    };
  } else if (allocationSourceParsed !== 'Dealer Allocation' && gmpCode !== true) {
    return {
      attributes: getNoDealerAllocationGmpNAttributes(vehicle),
      ...title,
    };
  }

  return null;
};

export const vehicleAssignment = ({
  vehicle,
  isPrint,
  searchContextValues,
  setSelectedTab = () => null,
}) => {
  if (
    !isVehicleDetailsDataExist([
      vehicle?.generalInfo?.dealerAssignmentInfo?.dealerAllocationHistory?.length,
      vehicle?.generalInfo?.dealerAssignmentInfo?.allocationInProcessDescription,
      vehicle?.generalInfo?.dealerAssignmentInfo?.allocationNumber,
      vehicle?.generalInfo?.dealerAssignmentInfo?.allocationDateFmt,
      vehicle?.generalInfo?.dealerInfo?.currentRegionCode,
      vehicle?.generalInfo?.dealerInfo?.currentRegionName,
      vehicle?.generalInfo?.dealerInfo?.currentDealerCode,
      vehicle?.generalInfo?.dealerInfo?.currentDealerName,
      vehicle?.generalInfo?.dealerInfo?.shipToRegionCode,
      vehicle?.generalInfo?.dealerInfo?.shipToRegionName,
      vehicle?.generalInfo?.dealerInfo?.shipToDealerCode,
      vehicle?.generalInfo?.dealerInfo?.shipToDealerName,
    ])
  ) {
    return null;
  }

  const gmpCode = get(vehicle, 'generalInfo.dealerAssignmentInfo.gmpCode');

  const allocationSource = get(
    vehicle,
    'generalInfo.dealerAssignmentInfo.dealerAllocationHistory[0].allocationSource',
  );

  const allocationSourceParsed =
    allocationSource === 'Dealer Trade' ||
    (allocationSource === 'Dealer Allocation' && gmpCode === true)
      ? formatDataValue({ value: allocationSource })
      : 'N';

  return {
    vehicleAssignmentHero: getVehicleAssignmentHeroData({
      vehicle,
      isPrint,
      searchContextValues,
      setSelectedTab,
    }),
    current: getCurrentData({ vehicle }),
    shipTo: getAreaData({ vehicle }),
    history: map(
      vehicle?.generalInfo?.dealerAssignmentInfo?.dealerAllocationHistory,
      (assignment, index) => ({
        id: index,
        attributes: getRowAttributes({
          assignment,
          index,
          vehicle,
          setSelectedTab,
          isPrint,
          searchContextValues,
        }),
        details: getRowDetails({
          allocationSourceParsed,
          index,
          vehicle,
        }),
      }),
    ),
  };
};
